import { useRef, useEffect, useState } from 'react';
import { useSmsMessages, usePatchSmsConversation, useSendSms } from './useSmsConversations';
import { useSms } from '../store/sms';
import type { SmsConversation } from '@marnic/shared';

interface Props {
  conversation: SmsConversation | null;
}

const STATUS_ICON: Record<string, string> = {
  queued: '○',
  sent: '✓',
  delivered: '✓✓',
  read: '✓✓',
  failed: '✗',
};

export function SmsChatPanel({ conversation }: Props): JSX.Element {
  const endRef = useRef<HTMLDivElement>(null);
  const messagesByConversation = useSms((s) => s.messagesByConversation);
  const markRead = useSms((s) => s.markConversationRead);

  const convId = conversation?.id ?? null;
  const { isLoading } = useSmsMessages(convId);
  const { mutate: patchConv } = usePatchSmsConversation(convId ?? '');
  const send = useSendSms();

  const [draft, setDraft] = useState('');

  const messages = convId ? (messagesByConversation[convId] ?? []) : [];

  useEffect(() => {
    if (convId && conversation && conversation.unreadCount > 0) {
      markRead(convId);
      patchConv({ markRead: true });
    }
  }, [convId, conversation, markRead, patchConv]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  if (!conversation) {
    return (
      <div className="rounded-md border border-dashed border-ink-200 p-8 text-center text-sm text-ink-400">
        Select a conversation to open the chat.
      </div>
    );
  }

  const submit = (): void => {
    const body = draft.trim();
    if (!body || !conversation.lead) return;
    send.mutate(
      { leadId: conversation.leadId, body },
      { onSuccess: () => setDraft('') },
    );
  };

  return (
    <div className="card flex flex-col" style={{ height: '600px' }}>
      {/* Header */}
      <div className="shrink-0 border-b border-ink-200 px-4 py-3 flex items-center justify-between">
        <div>
          <div className="font-medium text-sm">
            {conversation.lead?.firstName} {conversation.lead?.lastName}
            {conversation.lead?.company ? ` · ${conversation.lead.company}` : ''}
          </div>
          <div className="text-xs text-ink-500 font-mono">{conversation.lead?.phoneE164}</div>
        </div>
        {conversation.status === 'open' ? (
          <button type="button" className="btn-secondary text-xs" onClick={() => patchConv({ status: 'closed' })}>
            Close
          </button>
        ) : (
          <button type="button" className="btn-secondary text-xs" onClick={() => patchConv({ status: 'open' })}>
            Reopen
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {isLoading && messages.length === 0 && (
          <div className="text-center text-xs text-ink-400 pt-8">Loading messages…</div>
        )}
        {!isLoading && messages.length === 0 && (
          <div className="text-center text-xs text-ink-400 pt-8">No messages yet.</div>
        )}
        {messages.map((m) => {
          const isOut = m.direction === 'outbound';
          return (
            <div key={m.id} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                  isOut
                    ? m.status === 'failed'
                      ? 'bg-red-100 text-red-900 rounded-br-sm'
                      : 'bg-brand-600 text-white rounded-br-sm'
                    : 'bg-ink-100 text-ink-900 rounded-bl-sm'
                }`}
              >
                <p className="whitespace-pre-wrap break-words">{m.body}</p>
                {m.mediaUrl && (
                  <a href={m.mediaUrl} target="_blank" rel="noreferrer" className="text-xs underline opacity-70 mt-1 block">
                    View media
                  </a>
                )}
                <div
                  className={`flex items-center gap-1 mt-1 text-xs ${
                    isOut && m.status !== 'failed' ? 'text-white/70 justify-end' : 'text-ink-500 justify-end'
                  }`}
                >
                  <span>
                    {new Date(m.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {isOut && <span title={m.status}>{STATUS_ICON[m.status] ?? '○'}</span>}
                </div>
                {isOut && m.status === 'failed' && m.errorCode && (
                  <div className="text-[11px] text-red-700 mt-0.5">Failed: {m.errorCode}</div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <div className="shrink-0 border-t border-ink-200 p-3">
        <div className="flex items-end gap-2">
          <textarea
            className="input min-h-[42px] max-h-32 flex-1 text-sm"
            placeholder="Type an SMS…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
          <button
            type="button"
            className="btn-primary shrink-0"
            onClick={submit}
            disabled={send.isPending || !draft.trim()}
          >
            {send.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
        {send.isError && (
          <p className="mt-1 text-xs text-red-600">Could not send — check the number is SMS-capable.</p>
        )}
      </div>
    </div>
  );
}
